import { withSentry } from './sentry-loader'

export function trackPermitCreated(
  permitId: string,
  projectId: string,
  cityId: string,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('permit.created', 1, { attributes: { cityId } })
    Sentry.addBreadcrumb({
      category: 'permit',
      message: 'Permit created',
      data: { permitId, projectId, cityId },
      level: 'info',
    })
    Sentry.setContext('permit', { permitId, projectId, cityId })
  })
}

export function trackPermitCompleted(
  permitId: string,
  completed: boolean,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count(
      completed ? 'permit.completed' : 'permit.marked_incomplete',
      1,
    )
    Sentry.addBreadcrumb({
      category: 'permit',
      message: completed
        ? 'Permit marked as completed'
        : 'Permit marked as incomplete',
      data: { permitId, completed },
      level: 'info',
    })
  })
}

export function trackPermitResubmission(permitId: string): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('permit.resubmitted', 1)
    Sentry.addBreadcrumb({
      category: 'permit',
      message: 'Permit resubmission started',
      data: { permitId },
      level: 'info',
    })
  })
}

export function trackSubmittalCreated(
  submittalId: string,
  permitId: string,
  requiredDocCount: number,
  otherDocCount: number,
  isEdit: boolean,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count(isEdit ? 'submittal.rerun' : 'submittal.created', 1)
    Sentry.metrics.distribution(
      'submittal.required_document_count',
      requiredDocCount,
      {
        unit: 'none',
      },
    )
    Sentry.addBreadcrumb({
      category: 'submittal',
      message: isEdit ? 'Submittal rerun (admin check)' : 'Submittal created',
      data: { submittalId, permitId, requiredDocCount, otherDocCount },
      level: 'info',
    })
    Sentry.setContext('submittal', { submittalId, permitId })
  })
}

export function trackDocumentUpload(
  fileName: string,
  fileSizeBytes: number,
  uploadType: 'required' | 'other',
  durationMs: number,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.distribution('document.upload.duration_ms', durationMs, {
      unit: 'millisecond',
      attributes: { uploadType },
    })
    Sentry.metrics.distribution('document.upload.size_bytes', fileSizeBytes, {
      unit: 'byte',
      attributes: { uploadType },
    })
    Sentry.addBreadcrumb({
      category: 'upload',
      message: `Document uploaded: ${fileName}`,
      data: { fileName, fileSizeBytes, uploadType, durationMs },
      level: 'info',
    })
  })
}

export function trackDocumentUploadFailed(
  fileName: string,
  fileSizeBytes: number,
  uploadType: 'required' | 'other',
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('document.upload.failed', 1, {
      attributes: { uploadType },
    })
    Sentry.addBreadcrumb({
      category: 'upload',
      message: `Document upload failed: ${fileName}`,
      data: { fileName, fileSizeBytes, uploadType },
      level: 'error',
    })
  })
}

export function trackAdminCheckStarted(
  submittalId: string,
  type: 'initial' | 'rerun',
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('admin_check.started', 1, { attributes: { type } })
    Sentry.addBreadcrumb({
      category: 'ai',
      message: `Admin check started (${type})`,
      data: { submittalId, type },
      level: 'info',
    })
  })
}

export function trackAIJobCompleted(
  trackerId: string,
  status: string | undefined,
  durationMs: number,
  totalJobs: number | undefined,
  failedJobs: number | undefined,
): void {
  withSentry((Sentry) => {
    const failed = failedJobs ?? 0
    const succeeded = failed === 0 && status?.toLowerCase() !== 'failed'
    const outcome = succeeded ? 'success' : 'failure'

    Sentry.metrics.distribution('ai.job.duration_ms', durationMs, {
      unit: 'millisecond',
      attributes: { status: status ?? 'unknown', outcome },
    })
    Sentry.metrics.count(succeeded ? 'ai.job.succeeded' : 'ai.job.failed', 1)

    if (totalJobs !== undefined) {
      Sentry.metrics.distribution('ai.job.total_jobs', totalJobs, {
        unit: 'none',
      })
    }
    if (failed > 0) {
      Sentry.metrics.distribution('ai.job.failed_jobs', failed, {
        unit: 'none',
      })
    }

    Sentry.addBreadcrumb({
      category: 'ai',
      message: `AI job ${outcome}: ${trackerId}`,
      data: { trackerId, status, durationMs, totalJobs, failedJobs },
      level: succeeded ? 'info' : 'warning',
    })
  })
}

export function trackAIJobError(trackerId: string, reason: string): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('ai.job.error', 1)
    Sentry.addBreadcrumb({
      category: 'ai',
      message: 'AI job error: ' + reason,
      data: { trackerId, reason },
      level: 'error',
    })
  })
}

export function trackSitePlanJobsStarted(
  submittalId: string,
  documentCount: number | undefined,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('site_plan_jobs.started', 1)
    if (documentCount !== undefined) {
      Sentry.metrics.distribution(
        'site_plan_jobs.document_count',
        documentCount,
        {
          unit: 'none',
        },
      )
    }
    Sentry.addBreadcrumb({
      category: 'ai',
      message: 'Site plan jobs started',
      data: { submittalId, documentCount },
      level: 'info',
    })
  })
}

export function trackMasterTrackerCompleted(
  masterTrackerId: string,
  documentCount: number,
  durationMs: number,
  anyFailed: boolean,
): void {
  withSentry((Sentry) => {
    const outcome = anyFailed ? 'partial_failure' : 'success'
    Sentry.metrics.distribution('ai.master_tracker.duration_ms', durationMs, {
      unit: 'millisecond',
      attributes: { outcome },
    })
    Sentry.metrics.distribution(
      'ai.master_tracker.document_count',
      documentCount,
      {
        unit: 'none',
      },
    )
    Sentry.metrics.count(
      anyFailed
        ? 'ai.master_tracker.partial_failure'
        : 'ai.master_tracker.succeeded',
      1,
    )
    Sentry.addBreadcrumb({
      category: 'ai',
      message: `Master tracker completed (${outcome})`,
      data: { masterTrackerId, documentCount, durationMs, anyFailed },
      level: anyFailed ? 'warning' : 'info',
    })
  })
}

export function trackPlaygroundOpened(
  submittalId: string,
  documentCount: number,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('playground.opened', 1)
    Sentry.metrics.distribution('playground.document_count', documentCount, {
      unit: 'none',
    })
    Sentry.setContext('playground', { submittalId, documentCount })
    Sentry.addBreadcrumb({
      category: 'playground',
      message: 'Playground opened',
      data: { submittalId, documentCount },
      level: 'info',
    })
  })
}

export function trackCommentAdded(type: 'manual' | 'ai'): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('comment.added', 1, { attributes: { type } })
    Sentry.addBreadcrumb({
      category: 'playground',
      message: `Comment added (${type})`,
      level: 'info',
    })
  })
}

export function trackCommentResolved(commentId: string): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('comment.resolved', 1)
    Sentry.addBreadcrumb({
      category: 'playground',
      message: 'Comment resolved',
      data: { commentId },
      level: 'info',
    })
  })
}

export function trackMeasurementAdded(
  measurementType: 'line' | 'polygon' | 'circle',
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('measurement.added', 1, {
      attributes: { measurementType },
    })
    Sentry.addBreadcrumb({
      category: 'playground',
      message: `Measurement added: ${measurementType}`,
      level: 'info',
    })
  })
}

export function trackTemplateCreated(cityId: string): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('template.created', 1, { attributes: { cityId } })
    Sentry.addBreadcrumb({
      category: 'template',
      message: 'Permit template created',
      data: { cityId },
      level: 'info',
    })
  })
}

export function trackTemplateEdited(templateId: string): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('template.edited', 1)
    Sentry.addBreadcrumb({
      category: 'template',
      message: 'Permit template edited',
      data: { templateId },
      level: 'info',
    })
  })
}

export function trackNotificationRead(count: number = 1): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('notification.read', count)
    Sentry.addBreadcrumb({
      category: 'notification',
      message:
        count > 1
          ? `${count} notifications marked read`
          : 'Notification marked read',
      level: 'info',
    })
  })
}

export function trackProjectCreated(cityId: string): void {
  withSentry((Sentry) => {
    Sentry.metrics.count('project.created', 1, { attributes: { cityId } })
    Sentry.addBreadcrumb({
      category: 'project',
      message: 'Project created',
      data: { cityId },
      level: 'info',
    })
  })
}

export function trackAdminReportSaved(
  submittalId: string,
  isNew: boolean,
): void {
  withSentry((Sentry) => {
    Sentry.metrics.count(
      isNew ? 'admin_report.created' : 'admin_report.updated',
      1,
    )
    Sentry.addBreadcrumb({
      category: 'admin-report',
      message: isNew ? 'Admin report created' : 'Admin report updated',
      data: { submittalId },
      level: 'info',
    })
  })
}
